import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for persisting state to localStorage
 * @param {string} key - The localStorage key
 * @param {any} defaultValue - Default value if nothing is stored
 * @param {Array} validValues - Optional array of valid values for validation
 * @returns {[any, function]} - [value, setValue] similar to useState
 */
export const usePersistence = (key, defaultValue, validValues = null) => {
  const [value, setValue] = useState(() => {
    try {
      const savedValue = localStorage.getItem(key);
      if (savedValue === null) return defaultValue;
      
      const parsedValue = JSON.parse(savedValue);
      
      // If validValues is provided, validate the parsed value
      if (validValues && !validValues.includes(parsedValue)) {
        return defaultValue;
      }
      
      return parsedValue;
    } catch (error) {
      console.warn(`Failed to load persisted value for key "${key}":`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to persist value for key "${key}":`, error);
    }
  }, [key, value]);

  return [value, setValue];
};

/**
 * Hook specifically for tab persistence with URL and localStorage sync.
 * Integrates with React Router so browser back/forward and shared links work correctly.
 *
 * Priority: URL (?tab=) > localStorage > defaultTab
 *
 * @param {string} componentName - Name of the component (for unique localStorage key)
 * @param {string} defaultTab - Default tab to show when no URL/localStorage value
 * @param {Array} validTabs - Array of valid tab values (validates URL and localStorage)
 * @param {string} paramName - URL query param name (default: 'tab')
 * @returns {[string, function]} - [activeTab, setActiveTab]
 */
export const useTabPersistence = (componentName, defaultTab, validTabs, paramName = 'tab') => {
  const [searchParams, setSearchParams] = useSearchParams();
  const key = `${componentName}-active-tab`;

  const urlTab = searchParams.get(paramName);
  const isValidUrlTab = urlTab !== null && urlTab !== '' && (!validTabs || validTabs.includes(urlTab));

  const readResolvedTab = () => {
    if (isValidUrlTab) return urlTab;
    try {
      const saved = localStorage.getItem(key);
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        if (!validTabs || validTabs.includes(parsed)) return parsed;
      }
    } catch {}
    return defaultTab;
  };

  const [value, setValue] = useState(readResolvedTab);

  // Sync FROM URL when it changes externally (browser back/forward, direct nav)
  useEffect(() => {
    if (isValidUrlTab && urlTab !== value) setValue(urlTab);
  }, [urlTab, isValidUrlTab]);

  // Sync TO URL once on mount when URL has no valid tab (use localStorage or default)
  const hasInitialSync = useRef(false);
  useEffect(() => {
    if (!isValidUrlTab && !hasInitialSync.current) {
      hasInitialSync.current = true;
      let resolved = defaultTab;
      try {
        const saved = localStorage.getItem(key);
        if (saved !== null) {
          const parsed = JSON.parse(saved);
          if (!validTabs || validTabs.includes(parsed)) resolved = parsed;
        }
      } catch {}
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, resolved);
          return next;
        },
        { replace: true }
      );
    }
  }, [isValidUrlTab, defaultTab, key, validTabs, paramName, setSearchParams]);

  const setTab = useCallback(
    (newTab) => {
      if (validTabs && !validTabs.includes(newTab)) return;
      setValue(newTab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, newTab);
          return next;
        },
        { replace: true }
      );
      try {
        localStorage.setItem(key, JSON.stringify(newTab));
      } catch {}
    },
    [paramName, key, setSearchParams, validTabs]
  );

  return [value, setTab];
};

/**
 * Hook for persisting multiple related states
 * Note: This hook stores all states in a single localStorage entry to avoid Rules of Hooks violations.
 * For better performance and clarity, consider using individual usePersistence calls instead.
 * @param {string} componentName - Name of the component
 * @param {Object} defaultStates - Object with default state values
 * @param {Object} validValues - Object with arrays of valid values for each state
 * @returns {Object} - Object with state values and setters
 */
export const useMultiStatePersistence = (componentName, defaultStates, validValues = {}) => {
  const stateKeys = Object.keys(defaultStates);
  
  // Always call hooks at the top level - no conditional returns
  const [allStates, setAllStates] = useState(() => {
    try {
      const key = `${componentName}-multi-state`;
      const savedValue = localStorage.getItem(key);
      if (savedValue === null) return defaultStates;
      
      const parsedValue = JSON.parse(savedValue);
      
      // Validate each state value
      const validatedStates = {};
      stateKeys.forEach(stateKey => {
        const savedValue = parsedValue[stateKey];
        const validStateValues = validValues[stateKey];
        
        if (validStateValues && !validStateValues.includes(savedValue)) {
          validatedStates[stateKey] = defaultStates[stateKey];
        } else {
          validatedStates[stateKey] = savedValue !== undefined ? savedValue : defaultStates[stateKey];
        }
      });
      
      return validatedStates;
    } catch (error) {
      console.warn(`Failed to load persisted multi-state for key "${componentName}":`, error);
      return defaultStates;
    }
  });

  useEffect(() => {
    try {
      const key = `${componentName}-multi-state`;
      localStorage.setItem(key, JSON.stringify(allStates));
    } catch (error) {
      console.warn(`Failed to persist multi-state for key "${componentName}":`, error);
    }
  }, [componentName, allStates]);

  // Create setters for each state
  const result = {};
  stateKeys.forEach(stateKey => {
    result[stateKey] = [
      allStates[stateKey],
      (newValue) => {
        setAllStates(prev => ({
          ...prev,
          [stateKey]: newValue
        }));
      }
    ];
  });

  return result;
};

/**
 * Hook for persisting filter states (commonly used pattern)
 * @param {string} componentName - Name of the component
 * @param {Object} defaultFilters - Default filter values
 * @returns {Object} - Object with filter states and setters
 * @deprecated Use individual usePersistence calls instead to avoid Rules of Hooks violations
 */
export const useFilterPersistence = (componentName, defaultFilters) => {
  return useMultiStatePersistence(componentName, defaultFilters);
};

/**
 * Hook for persisting UI preferences (view modes, expanded states, etc.)
 * @param {string} componentName - Name of the component
 * @param {Object} defaultPreferences - Default UI preferences
 * @returns {Object} - Object with preference states and setters
 * @deprecated Use individual usePersistence calls instead to avoid Rules of Hooks violations
 */
export const useUIPreferencePersistence = (componentName, defaultPreferences) => {
  return useMultiStatePersistence(componentName, defaultPreferences);
};

/**
 * Hook for persisting nested tab states with URL synchronization
 * Handles multiple nested URL parameters atomically (e.g., ?tab=documents&subTab=uploads&category=identity)
 * 
 * @param {string} componentName - Name of the component (for unique localStorage keys)
 * @param {string} parentTabValue - Current parent tab value (e.g., 'documents'). Nested params only persist when parent matches expected value
 * @param {Object} config - Configuration object defining nested states
 * @param {Object} config[stateKey] - Each state configuration
 * @param {any} config[stateKey].default - Default value for this state
 * @param {Array} config[stateKey].valid - Array of valid values for validation
 * @param {string} config[stateKey].param - URL parameter name (e.g., 'subTab', 'category')
 * 
 * @returns {Object} - Object with state values and setters: { stateKey: [value, setValue], ... }
 * 
 * @example
 * const { subTab, category } = useNestedTabPersistence('profile-documents', activeTab, {
 *   subTab: { default: 'letters', valid: ['letters', 'uploads'], param: 'subTab' },
 *   category: { default: null, valid: ['identity', 'education', ...], param: 'category' }
 * });
 */
export const useNestedTabPersistence = (componentName, parentTabValue, config) => {
  const stateKeys = Object.keys(config);
  
  // Initialize all states from URL → localStorage → default
  const readInitialStates = () => {
    const states = {};
    const urlParams = new URLSearchParams(window.location.search);
    
    stateKeys.forEach(stateKey => {
      const stateConfig = config[stateKey];
      const { default: defaultValue, valid, param } = stateConfig;
      const localStorageKey = `${componentName}-${stateKey}`;
      
      // Priority 1: URL parameter
      try {
        const urlValue = urlParams.get(param);
        if (urlValue !== null && (!valid || valid.includes(urlValue))) {
          states[stateKey] = urlValue;
          return; // Found valid URL value, skip to next state
        }
      } catch {}
      
      // Priority 2: localStorage
      try {
        const savedValue = localStorage.getItem(localStorageKey);
        if (savedValue !== null) {
          const parsed = JSON.parse(savedValue);
          if (!valid || valid.includes(parsed)) {
            states[stateKey] = parsed;
            return; // Found valid localStorage value, skip to next state
          }
        }
      } catch {}
      
      // Priority 3: Default
      states[stateKey] = defaultValue;
    });
    
    return states;
  };
  
  const [allStates, setAllStates] = useState(readInitialStates);
  
  // Persist to localStorage whenever states change
  useEffect(() => {
    stateKeys.forEach(stateKey => {
      const localStorageKey = `${componentName}-${stateKey}`;
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(allStates[stateKey]));
      } catch (error) {
        console.warn(`Failed to persist ${stateKey} to localStorage:`, error);
      }
    });
  }, [componentName, allStates, stateKeys]);
  
  // Update URL atomically when any state changes
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      let urlChanged = false;
      
      // If parent tab is not active (null/undefined), clean up all nested params
      if (parentTabValue === null || parentTabValue === undefined) {
        stateKeys.forEach(stateKey => {
          const { param } = config[stateKey];
          if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            urlChanged = true;
          }
        });
      } else {
        // Parent tab is active - update nested params
        stateKeys.forEach(stateKey => {
          const stateConfig = config[stateKey];
          const { param, valid } = stateConfig;
          const currentValue = allStates[stateKey];
          
          // Only set URL param if value is not null/undefined and is valid
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            if (!valid || valid.includes(currentValue)) {
              url.searchParams.set(param, currentValue);
              urlChanged = true;
            }
          } else {
            // Remove param if value is null/undefined/empty
            if (url.searchParams.has(param)) {
              url.searchParams.delete(param);
              urlChanged = true;
            }
          }
        });
      }
      
      if (urlChanged) {
        window.history.replaceState({}, document.title, url.toString());
      }
    } catch (error) {
      console.warn('Failed to update URL with nested tab state:', error);
    }
  }, [componentName, allStates, parentTabValue, stateKeys, config]);
  
  // Create setters for each state
  const result = {};
  stateKeys.forEach(stateKey => {
    result[stateKey] = [
      allStates[stateKey],
      (newValue) => {
        const stateConfig = config[stateKey];
        const { valid, default: defaultValue } = stateConfig;
        
        // Validate new value
        if (valid && !valid.includes(newValue)) {
          console.warn(`Invalid value for ${stateKey}: ${newValue}. Using default.`);
          setAllStates(prev => ({
            ...prev,
            [stateKey]: defaultValue
          }));
          return;
        }
        
        setAllStates(prev => ({
          ...prev,
          [stateKey]: newValue
        }));
      }
    ];
  });
  
  return result;
};
